import React, { useState, useCallback, useRef } from 'react';
import type {
  UseFormGetValues,
  UseFormTrigger,
  UseFormReset,
  Control,
} from 'react-hook-form';
import { useFormState } from 'react-hook-form';
import type { IntakeResponse } from '../../services/api';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import type { CreatePatientFormData, SaveStatus } from './types';
import { LANGUAGE_OPTIONS, OTHER_LANGUAGE_KEY } from './constants';

interface UseFormAutoSaveOptions {
  intakeId: string | undefined;
  selectedIntake: IntakeRecord | null;
  updateIntakeRecord: (
    id: string,
    data: Partial<IntakeResponse>
  ) => Promise<IntakeRecord>;
  control: Control<CreatePatientFormData>;
  getValues: UseFormGetValues<CreatePatientFormData>;
  trigger: UseFormTrigger<CreatePatientFormData>;
  reset: UseFormReset<CreatePatientFormData>;
}

interface UseFormAutoSaveReturn {
  saveStatus: SaveStatus;
  saveToServer: (updates: Partial<CreatePatientFormData>) => Promise<void>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;
}

/**
 * Hook for auto-saving form data on field blur.
 * After successful save, resets form baseline so isDirty compares against last saved values.
 */
export function useFormAutoSave({
  intakeId,
  selectedIntake,
  updateIntakeRecord,
  control,
  getValues,
  trigger,
  reset,
}: UseFormAutoSaveOptions): UseFormAutoSaveReturn {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { dirtyFields } = useFormState({ control });

  // Use ref to access latest dirtyFields in callbacks (avoids stale closure)
  const dirtyFieldsRef = useRef(dirtyFields);
  dirtyFieldsRef.current = dirtyFields;

  // Tracks in-flight saveToServer calls per field key using reference counts.
  // Prevents a blur-triggered save from racing against an onChange-triggered save that
  // already includes the correct full patch (e.g. kinnser_visit_type + kinnser_creation_type).
  // Reference counting (vs a plain Set) handles concurrent saves sharing the same key:
  // the key stays tracked until every overlapping save completes.
  const savingFieldsRef = useRef<Map<string, number>>(new Map());

  // Every save runs behind the one before it. Two saves can carry the same
  // field: an external referral pick writes `kinnser_internal_referral_id`
  // alongside the external id, and a manual internal pick straight after writes
  // that same field alone. Issued together, the two requests can land in either
  // order, so the server can keep the auto-selected default while the screen
  // shows the user's later choice, and the push then sends the wrong
  // attribution to WellSky. A chain makes the last write on screen the last
  // write sent.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());

  // Saves queued or in flight. The status stays `saving` until the queue
  // drains, so a caller that blocks on `saving` (the Push button) cannot fire
  // between two saves of one edit.
  const pendingSavesRef = useRef(0);

  // Fields whose last save attempt failed and which have not been saved since.
  //
  // A successful save resets the form baseline to the values on screen, which
  // clears every dirty flag, including flags for fields the server never took.
  // Two things break without this set. A blur can no longer retry the failed
  // field, because the blur save is guarded on the dirty flag that the reset
  // just cleared. And a later successful save of any other field ends its own
  // burst cleanly, turning the indicator green and unblocking Push while the
  // server still holds older values than the screen shows.
  //
  // Tracking the fields themselves fixes both: the status is `error` for as
  // long as the set holds anything, whatever else saves in the meantime, and a
  // blur on a field in the set saves it even though the form calls it clean.
  const unsavedFieldsRef = useRef<Set<string>>(new Set());

  const saveToServer = useCallback(
    (updates: Partial<CreatePatientFormData>): Promise<void> => {
      if (!intakeId || !selectedIntake) return Promise.resolve();

      const fieldKeys = Object.keys(updates);
      fieldKeys.forEach(k =>
        savingFieldsRef.current.set(
          k,
          (savingFieldsRef.current.get(k) ?? 0) + 1
        )
      );

      pendingSavesRef.current += 1;

      // Set here, not inside the queued work: the button must block from the
      // moment the save is asked for, not from the moment it reaches the wire.
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      setSaveStatus('saving');

      const runSave = async () => {
        try {
          // Send only the changed fields
          await updateIntakeRecord(
            intakeId,
            updates as Partial<IntakeResponse>
          );

          // Reset form baseline to current values so isDirty compares against what was just saved
          const currentValues = getValues();
          reset(currentValues, { keepValues: true, keepErrors: true });

          fieldKeys.forEach(k => unsavedFieldsRef.current.delete(k));
        } catch (error) {
          console.error('Error saving intake:', error);
          fieldKeys.forEach(k => unsavedFieldsRef.current.add(k));
        } finally {
          fieldKeys.forEach(k => {
            const count = (savingFieldsRef.current.get(k) ?? 1) - 1;
            if (count <= 0) {
              savingFieldsRef.current.delete(k);
            } else {
              savingFieldsRef.current.set(k, count);
            }
          });

          pendingSavesRef.current -= 1;

          // Report only once the whole queue has drained. A per-save report
          // would flip the status to `saved` while later saves of the same edit
          // were still unsent.
          if (pendingSavesRef.current === 0) {
            if (unsavedFieldsRef.current.size > 0) {
              // Deliberately not cleared on a timer, and not cleared by a
              // later save of some other field either. The server does not
              // hold what the screen shows until this set is empty, and the
              // Push button reads this status and stays blocked meanwhile.
              setSaveStatus('error');
            } else {
              setSaveStatus('saved');
              saveTimeoutRef.current = setTimeout(
                () => setSaveStatus('idle'),
                2000
              );
            }
          }
        }
      };

      // `then(runSave, runSave)` and not `finally`: a rejected link must not
      // stop the ones behind it, and runSave never rejects.
      const queued = saveChainRef.current.then(runSave, runSave);
      saveChainRef.current = queued;
      return queued;
    },
    [intakeId, selectedIntake, updateIntakeRecord, getValues, reset]
  );

  const handleFieldBlur = useCallback(
    (fieldEvent: React.FocusEvent<HTMLElement>) => {
      const target = fieldEvent.target as HTMLInputElement;
      // Try name attribute first, fall back to data-field-name attribute
      const fieldName = (target.name ||
        target.dataset.fieldName) as keyof CreatePatientFormData;

      if (!fieldName) {
        console.warn(
          'handleFieldBlur: Could not determine field name',
          fieldName
        );
        return;
      }

      // Use setTimeout to defer work so the blur event completes immediately (dropdown closes)
      setTimeout(async () => {
        // Skip save if field hasn't changed (use ref for latest value).
        // A field whose save failed is exempt: the baseline reset from some
        // later successful save cleared its dirty flag, so the flag alone
        // would make the retry impossible.
        if (
          !dirtyFieldsRef.current[fieldName] &&
          !unsavedFieldsRef.current.has(fieldName)
        ) {
          return;
        }

        // Skip save if this field is already being saved via an explicit saveToServer call
        // (e.g. visit type onChange saves kinnser_visit_type + kinnser_creation_type together;
        // without this guard the blur would fire a second save with only kinnser_visit_type,
        // dropping kinnser_creation_type and causing a race condition).
        if (savingFieldsRef.current.has(fieldName)) {
          return;
        }

        const isFieldValid = await trigger(fieldName);
        if (!isFieldValid) {
          return;
        }

        const fieldValue = getValues(fieldName);

        // Handle special case for language field - update patient_primary_language
        const updates: Partial<CreatePatientFormData> = {
          [fieldName]: fieldValue,
        };

        if (fieldName === 'kinnser_language_key') {
          const languageKey = fieldValue as string;
          let patientPrimaryLanguage: string | null = null;

          if (languageKey && languageKey !== OTHER_LANGUAGE_KEY) {
            // Find the language label from the options
            const languageOption = LANGUAGE_OPTIONS.find(
              option => option.value === languageKey
            );
            patientPrimaryLanguage = languageOption?.label || null;
          } else if (languageKey === OTHER_LANGUAGE_KEY) {
            // Use the content of kinnser_other_language field
            const otherLanguage = getValues('kinnser_other_language');
            patientPrimaryLanguage = otherLanguage || null;
          }

          updates.patient_primary_language = patientPrimaryLanguage;
        }

        if (
          fieldName === 'kinnser_other_language' &&
          getValues('kinnser_language_key') === OTHER_LANGUAGE_KEY
        ) {
          const otherLanguage = fieldValue as string;
          updates.patient_primary_language = otherLanguage;
        }

        // When case manager changes, also sync approver fields to the same value
        if (fieldName === 'kinnser_case_manager_id' && fieldValue) {
          updates.kinnser_oasis_approver_id = fieldValue as string;
          updates.kinnser_pt_approver_id = fieldValue as string;
          updates.kinnser_ot_approver_id = fieldValue as string;
          updates.kinnser_st_approver_id = fieldValue as string;
        }

        // Save the changed fields
        saveToServer(updates);
      }, 0);
    },
    [trigger, getValues, saveToServer]
  );

  return {
    saveStatus,
    saveToServer,
    handleFieldBlur,
  };
}
